import type { ArticleDetailItem, ProductItem } from '~/types/site-api'

/**
 * 站点 SEO 元数据集中管理。
 * 域名通过 `NUXT_PUBLIC_SITE_URL` 注入；正式文案和图片须以客户确认资料为准。
 */

/** 站点基础信息 */
export const siteSeo = {
  name: '海南兴科热带作物工程技术有限公司',
  shortName: '海南兴科',
  description:
    '海南兴科热带作物工程技术有限公司官网，专注热带特色作物科技与产业服务，连接科研平台、中试验证、产品研发与产业应用。',
  ogImage: '/logo/xingke-logo.png',
  keywords: [
    '海南兴科',
    '热带作物',
    '咖啡加工',
    '胡椒加工',
    '可可加工',
    '科研产业化',
    '中试验证',
    '成果转化',
    '兴科研学',
    '兴隆热带植物园',
  ],
}

/**
 * 获取当前站点域名。
 * 域名来自 `NUXT_PUBLIC_SITE_URL`，缺失时回退到正式域名。
 */
export function getSiteUrl() {
  return String(useRuntimeConfig().public.siteUrl || 'https://hainanxingke.com').replace(/\/$/, '')
}

/** 将站内路径规范为可直接拼接的路径。 */
function normalizeSitePath(path: string) {
  if (path === '/') return ''
  return path.startsWith('/') ? path : `/${path}`
}

/** 面包屑结构化数据输入项 */
export interface BreadcrumbJsonLdInput {
  name: string
  path: string
}

/** 文章结构化数据输入 */
export interface ArticleJsonLdInput {
  article: Pick<
    ArticleDetailItem,
    'author' | 'cover_url' | 'publish_date' | 'subtitle' | 'summary' | 'title'
  >
  category: string
  url: string
}

/** 产品结构化数据输入 */
export interface ProductJsonLdInput {
  product: Pick<
    ProductItem,
    | 'carton_spec'
    | 'category'
    | 'cover_url'
    | 'image_urls'
    | 'name'
    | 'price'
    | 'shelf_life'
    | 'spec'
    | 'subtitle'
    | 'summary'
    | 'unit'
  >
  url: string
}

/**
 * 生成站点内绝对地址。
 * @param path - 站内路径，根路径传 `/`
 * @param siteUrl - 在 setup 阶段读取的站点域名
 */
export function createSiteUrl(path: string, siteUrl: string) {
  return `${siteUrl}${normalizeSitePath(path)}`
}

/**
 * 生成企业 Organization 结构化数据。
 * 联系方式、地址与 Logo 须待客户确认后再补充，不使用原型占位信息。
 */
export function createOrganizationJsonLd(siteUrl: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteSeo.name,
    url: siteUrl,
    logo: createSiteUrl(siteSeo.ogImage, siteUrl),
    description: siteSeo.description,
  }
}

/**
 * 生成页面面包屑结构化数据。
 * @param items - 面包屑节点，只传入存在真实页面地址的节点
 */
export function createBreadcrumbJsonLd(items: BreadcrumbJsonLdInput[], siteUrl: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: createSiteUrl(item.path, siteUrl),
    })),
  }
}

/**
 * 生成文章详情结构化数据。
 * @param input - 页面实际展示的文章、栏目与详情地址
 */
export function createArticleJsonLd(input: ArticleJsonLdInput, siteUrl: string) {
  const { article, category, url } = input
  const description = article.summary || article.subtitle

  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    ...(description ? { description } : {}),
    ...(article.cover_url ? { image: article.cover_url } : {}),
    datePublished: article.publish_date,
    inLanguage: 'zh-CN',
    isAccessibleForFree: true,
    mainEntityOfPage: {
      '@id': url,
    },
    articleSection: category,
    author: article.author
      ? {
          '@type': 'Person',
          name: article.author,
        }
      : {
          '@type': 'Organization',
          name: siteSeo.name,
        },
    publisher: {
      '@type': 'Organization',
      name: siteSeo.name,
      logo: {
        '@type': 'ImageObject',
        url: createSiteUrl(siteSeo.ogImage, siteUrl),
      },
    },
  }
}

/**
 * 生成产品详情结构化数据。
 * @param input - 页面实际展示的产品与详情地址
 */
export function createProductJsonLd(input: ProductJsonLdInput) {
  const { product, url } = input
  const description = product.summary || product.subtitle
  const images = [...new Set([product.cover_url, ...product.image_urls].filter(Boolean))]
  const price = Number(product.price)
  const properties = [
    { name: '单位', value: product.unit },
    { name: '规格', value: product.spec },
    { name: '箱规', value: product.carton_spec },
    { name: '保质期', value: product.shelf_life },
  ].filter((property) => property.value)

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    ...(description ? { description } : {}),
    ...(images.length > 0 ? { image: images } : {}),
    category: product.category.name,
    ...(properties.length > 0
      ? {
          additionalProperty: properties.map((property) => ({
            '@type': 'PropertyValue',
            ...property,
          })),
        }
      : {}),
    ...(Number.isFinite(price) && product.price
      ? {
          offers: {
            '@type': 'Offer',
            price,
            priceCurrency: 'CNY',
            url,
          },
        }
      : {}),
  }
}

/** 页面 SEO 元数据接口 */
export interface PageSeo {
  description: string
  /** OG 图片路径，默认使用站点 logo */
  image?: string
  keywords?: string[]
  title: string
}

/** 各页面 SEO 元数据 */
export const pageSeoMap: Record<string, PageSeo> = {
  index: {
    title: '海南兴科 | 热带特色作物科技与产业服务',
    description:
      '海南兴科立足热带作物资源，围绕咖啡、胡椒、可可等特色作物，构建科研平台、中试验证、产品研发与产业服务相互连接的能力体系。',
    keywords: ['海南兴科', '热带作物科技', '科研产业化', '产品研发', '兴科研学', '企业官网'],
  },
  'company-profile': {
    title: '企业简介 | 海南兴科',
    description:
      '了解海南兴科的发展定位、企业文化与团队情况，连接热带作物科研资源、产品研发和产业服务。',
    keywords: ['企业简介', '海南兴科', '企业文化', '发展历程', '热带作物科研'],
  },
  'company-strength': {
    title: '科研与产业实力 | 海南兴科',
    description:
      '从科研创新、中试验证到成果转化与产品产业化，呈现海南兴科可回溯的能力路径与完整产业服务链条。',
    keywords: ['科研实力', '产业服务', '中试验证', '成果转化', '热带作物加工', '海南兴科'],
  },
  'company-qualifications': {
    title: '资质证书 | 海南兴科',
    description: '查看海南兴科公开发布的企业资质、认证证书、许可执照与荣誉资料。',
    keywords: ['资质证书', '企业资质', '认证证书', '企业荣誉', '海南兴科'],
  },
  news: {
    title: '最新动态 | 海南兴科',
    description: '浏览海南兴科公开发布的企业新闻、科研产业动态与团队故事。',
    keywords: ['海南兴科新闻', '企业新闻', '科研产业动态', '兴科精灵'],
  },
  video: {
    title: '视频展厅 | 海南兴科',
    description: '通过企业形象、乡村振兴与党建专题视频了解海南兴科的实践现场。',
    keywords: ['海南兴科视频', '企业形象', '乡村振兴', '党建专题'],
  },
  education: {
    title: '兴科研学 | 海南兴科',
    description: '浏览海南兴科研学活动与热带作物科普内容，了解活动安排与公开资料。',
    keywords: ['兴科研学', '研学活动', '科普活动', '热带作物科普'],
  },
  party: {
    title: '兴科党建 | 海南兴科',
    description: '查看海南兴科公开发布的党建动态、专题学习、党建活动与先锋故事。',
    keywords: ['兴科党建', '党建动态', '专题学习', '先锋故事'],
  },
  union: {
    title: '兴科工会 | 海南兴科',
    description: '查看海南兴科职工关怀、劳模先锋、职工风采与文体活动等公开内容。',
    keywords: ['兴科工会', '职工关怀', '劳模先锋', '职工风采'],
  },
  mall: {
    title: '兴科商城 | 海南兴科',
    description: '浏览海南兴科公开展示的热带作物特色产品，并从具体产品发起采购咨询。',
    keywords: ['兴科商城', '兴隆咖啡', '热带作物产品', '产品采购咨询'],
  },
}

/**
 * 获取页面 SEO 元数据，未配置时回退到站点默认值。
 * @param pageKey - 页面标识，对应 pageSeoMap 的 key
 */
export function getPageSeo(pageKey: string): PageSeo {
  return (
    pageSeoMap[pageKey] ?? {
      title: siteSeo.name,
      description: siteSeo.description,
    }
  )
}
